import type SessionService from '@efdigital/ll-addon-auth/services/session';
import type CoachService from '@efdigital/ll-xceed-v2/services/coach';
import type PartnershipMessagingService from '@efdigital/ll-xceed-v2/services/partnership-messaging';
import type { CoachingPlanner } from '@efdigital/ll-xceed-v2/utils/coaching/coachee/coaching-plan-manager';
import {
  loadCoacheePartnershipConversations,
  loadCoachPartnershipConversations,
  mergePartnershipConversationMessages,
} from '@efdigital/ll-xceed-v2/utils/coaching/load-partnership-conversations';
import type { MessagingRole } from '@efdigital/ll-xceed-v2/utils/coaching/partnership-messages';
import { cohortScopeKey } from '@efdigital/ll-xceed-v2/utils/coaching/scope-key';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import type { Store } from '@warp-drive/core';
import { resource, use } from 'ember-resources';

const REFRESH_INTERVAL_MS = 30_000;

interface PartnershipMessagingUnreadSyncSignature {
  Args: {
    role: MessagingRole;
    programId?: string;
    cohortId?: string;
    planner?: CoachingPlanner;
  };
}

export default class PartnershipMessagingUnreadSync extends Component<PartnershipMessagingUnreadSyncSignature> {
  @service declare coach: CoachService;
  @service declare partnershipMessaging: PartnershipMessagingService;
  @service declare router: RouterService;
  @service declare session: SessionService;
  @service declare store: Store;

  get scopeKey(): string {
    if (this.args.role === 'coach') {
      return 'coach';
    }

    return cohortScopeKey(this.args.programId!, this.args.cohortId!);
  }

  get shouldPoll(): boolean {
    return !this.router.currentRouteName?.endsWith('.messages.conversation');
  }

  // `isStale` is checked again after the fetch resolves, immediately before
  // any shared service state is touched, so a poll that outlives this
  // component's teardown (or is superseded by a newer sync) can't overwrite
  // fresher state with the data it fetched before it was cancelled.
  refreshConversations = async (
    isStale: () => boolean = () => false
  ): Promise<void> => {
    const { role, planner } = this.args;
    const scopeKey = this.scopeKey;

    const conversations =
      role === 'coach'
        ? await loadCoachPartnershipConversations(
            this.store,
            this.coach,
            this.session.cached.userId!
          )
        : planner
          ? await loadCoacheePartnershipConversations(this.store, planner)
          : [];

    if (isStale()) {
      return;
    }

    if (
      this.partnershipMessaging.scopeKey === scopeKey &&
      this.partnershipMessaging.role === role &&
      this.partnershipMessaging.conversations.length
    ) {
      if (
        mergePartnershipConversationMessages(
          this.partnershipMessaging.conversations,
          conversations
        )
      ) {
        this.partnershipMessaging.notifyMessagesChanged();
      }
      return;
    }

    this.partnershipMessaging.sync(conversations, role, scopeKey);
  };

  @use poll = resource(({ on }) => {
    if (!this.shouldPoll) {
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const run = async () => {
      if (cancelled || !this.shouldPoll) {
        return;
      }

      try {
        await this.refreshConversations(() => cancelled);
      } catch (error) {
        console.error(error);
      }

      if (!cancelled && this.shouldPoll) {
        timer = window.setTimeout(run, REFRESH_INTERVAL_MS);
      }
    };

    // The route that mounts this component already synced fresh data
    // (afterModel, or the Messages tab's own model()) before render, so the
    // first poll happens after the interval rather than immediately.
    timer = window.setTimeout(run, REFRESH_INTERVAL_MS);

    on.cleanup(() => {
      cancelled = true;
      if (timer) {
        window.clearTimeout(timer);
      }
    });
  });

  <template></template>
}
